// @ts-strict-ignore
import { EQpHttpStatusCode } from '@library/models/qp-http.models';
import { EQpProfile } from '@library/models/qp-profile.models';
import { LanguageHelperService } from '@library/services/locale/language-helper.service';
import { QpNotificationBarService } from '@library/services/qp-notification-bar/qp-notification-bar.service';
import { QpResponsiveService } from '@library/services/qp-responsive/qp-responsive.service';
import { CommonRoutes } from '@one/app/common-routes';
import { ActionsQueueSyncService } from '@one/app/components/main/services/actions-queue-sync.service';
import { IspRoutes } from '@one/app/pages/isp/isp-routes';
import { flushPendingNullableTestAnswers } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/store/isp-inspection-id-workflow-store.actions';
import { IspInspectionCleanService } from '@one/app/pages/isp/shared/services/isp-inspection-clean.service';
import { NetworkStatusAlertService } from '@one/app/services/network-status-alert/network-status-alert.service';
import { Account } from '@one/app/shared/classes/accounts/account';
import { EMenuPosition } from '@one/app/shared/models/menu/menu.models';
import { AccountService } from '@one/app/shared/services/account/account.service';
import { AuthenticationService } from '@one/app/shared/services/auth/authentication.service';
import { BrowserSessionCoordinatorService } from '@one/app/shared/services/browser-session/browser-session-coordinator.service';
import { PictureImportDiagnosticsService } from '@one/app/shared/services/pictures/picture-import-diagnostics/picture-import-diagnostics.service';
import { PictureImporterService } from '@one/app/shared/services/pictures/picture-importer/picture-importer.service';
import { DisplayedRoutesService } from '@one/app/shared/services/router/displayed-routes.service';
import { RouterHistoryService } from '@one/app/shared/services/router/router-history.service';
import { AppcuesService } from '@one/app/shared/services/third-party/appcues.service';
import { WINDOW } from '@one/app/shared/tokens/window.token';
import { IStoreState } from '@one/app/store/store.models';
import { Component, HostListener, inject, OnInit, DOCUMENT } from '@angular/core';
import { ActivatedRouteSnapshot, Event, NavigationEnd, NavigationError, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { QimaOptionalType } from '@qima/ngx-qima';
import { isNil } from 'lodash/index';
import { Observable, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'one-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class MainComponent implements OnInit {
  private static readonly orientationChangeDelayMs = 100;

  public readonly _languageHelperService = inject(LanguageHelperService);
  public readonly _router = inject(Router);
  public readonly _accountService = inject(AccountService);
  public readonly _displayedRoutesService = inject(DisplayedRoutesService);
  public readonly _networkStatusAlertService = inject(NetworkStatusAlertService);
  public readonly _routerHistoryService = inject(RouterHistoryService);
  public readonly _actionsQueueSyncService = inject(ActionsQueueSyncService);
  public readonly _ispInspectionCleanService = inject(IspInspectionCleanService);
  public readonly _authenticationService = inject(AuthenticationService);
  public readonly _translateService = inject(TranslateService);
  public readonly _window = inject(WINDOW);
  public readonly _document = inject(DOCUMENT);
  public readonly _appcuesService = inject(AppcuesService);
  public readonly _pictureImporterService = inject(PictureImporterService);
  public readonly _qpResponsiveService = inject(QpResponsiveService);
  public readonly _qpNotificationBarService = inject(QpNotificationBarService);
  public readonly _store = inject<Store<IStoreState>>(Store);

  public currentMenuPosition = EMenuPosition.COLLAPSE;
  public menuPositions = EMenuPosition;
  public routerUrl: string;
  public isMenuHidden = false;
  public isAuthenticated = false;
  public hasFluidContainer = true;
  public hasContainer = true;
  public isMobile$: Observable<boolean> = this._qpResponsiveService.isMobile$();

  private readonly _browserSessionCoordinatorService = inject(BrowserSessionCoordinatorService);
  private readonly _pictureImportDiagnosticsService = inject(PictureImportDiagnosticsService);
  private _lastViewportWidth = 0;
  private _hasShownMobileSyncWarning = false;

  public constructor() {
    this._actionsQueueSyncService.init();
    this._browserSessionCoordinatorService.init();
    void this._ispInspectionCleanService
      .clean()
      .then((purgedImageIds: string[]): void => this._actionsQueueSyncService.forgetPurgedFailedImages(purgedImageIds));
    this._router.events.pipe(untilDestroyed(this)).subscribe((): void => {
      this.routerUrl = this._router.url;
    });
    this._routerHistoryService.startListening();
    this._storeWindowInnerHeight();
    this._lastViewportWidth = this._window.visualViewport?.width ?? this._window.innerWidth;

    // iOS Safari fix: orientationchange fires before resize and viewport may not be settled
    this._window.addEventListener('orientationchange', (): void => {
      // Small delay to ensure viewport has settled after rotation
      setTimeout((): void => this._storeWindowInnerHeight(), MainComponent.orientationChangeDelayMs);
    });

    // PWA/Standalone mode fix: visualViewport API is more reliable for PWAs
    // Only update on width changes (rotation/zoom), ignore height-only changes (keyboard)
    if (this._window.visualViewport) {
      this._window.visualViewport.addEventListener('resize', (): void => {
        setTimeout((): void => this._storeWindowInnerHeightIfNotKeyboard(), MainComponent.orientationChangeDelayMs);
      });
    } else {
      // Fallback for browsers without visualViewport support
      this._window.addEventListener('resize', (): void => this._storeWindowInnerHeight());
    }
  }

  public get isIspRoute(): boolean {
    const path = (this.routerUrl ?? '').split(/[?#]/)[0];
    const ispRoot = IspRoutes.root();

    return path === ispRoot || path.startsWith(`${ispRoot}/`);
  }

  /**
   * The inspector home has no sub-navigation in the design: the subnav would only render a breadcrumb.
   */
  public get isInspectorHomeRoute(): boolean {
    return (this.routerUrl ?? '').split(/[?#]/)[0] === IspRoutes.home();
  }

  /**
   * Field-mode log-back lives in ISP chrome (dashboard, assignments, settings). Inspection
   * execution already has its own log-back control in the left panel.
   */
  public get shouldShowIspLogbackHeader(): boolean {
    if (!this.isIspRoute) {
      return false;
    }

    const path = (this.routerUrl ?? '').split(/[?#]/)[0];
    const inspectionRoot = `${IspRoutes.inspection()}/`;
    const consultationRoot = IspRoutes.inspectionConsultation();

    if (path === consultationRoot || path.startsWith(`${consultationRoot}/`)) {
      return true;
    }

    return path !== IspRoutes.inspection() && !path.startsWith(inspectionRoot);
  }

  @HostListener('window:beforeunload', ['$event'])
  public unloadNotification(event: BeforeUnloadEvent): void {
    if (
      this._actionsQueueSyncService.syncStackCount$.getValue() > 0 ||
      this._pictureImporterService.isAnalyzing ||
      this._pictureImporterService.isPromoting ||
      this._actionsQueueSyncService.isLocalPictureStagingActive
    ) {
      // The string only shows in IE and Edge
      event.returnValue = this._translateService.instant('global.exit-without-sync');
    }
  }

  @HostListener('document:visibilitychange')
  public onVisibilityChange(): void {
    if (this._document.visibilityState === 'visible') {
      this._hasShownMobileSyncWarning = false;

      return;
    }

    this._onAppBackgrounding();
  }

  @HostListener('window:pagehide')
  public onPageHide(): void {
    this._onAppBackgrounding();
  }

  public ngOnInit(): void {
    this._watchAuthentication();
    this._watchNavigationAndMenu();
    this._initAppcues();

    this._networkStatusAlertService.subscribeToNetworkEvents();
  }

  private _onAppBackgrounding(): void {
    this._pictureImportDiagnosticsService.flushInFlight();
    this._store.dispatch(flushPendingNullableTestAnswers());

    const hasPendingSync = this._actionsQueueSyncService.syncStackCount$.getValue() > 0 || this._pictureImporterService.isAnalyzing;

    if (!hasPendingSync || this._hasShownMobileSyncWarning) {
      return;
    }

    this._hasShownMobileSyncWarning = true;
    this._qpNotificationBarService.error(this._translateService.instant('global.exit-without-sync'));
  }

  private _watchAuthentication(): void {
    this._accountService
      .getAccount$()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (account: QimaOptionalType<Account>): void => {
          this.isAuthenticated = !isNil(account);
        },
        error: (): void => {
          this.isAuthenticated = false;
        },
      });
    void this._accountService.identity();
    this._authenticationService.setupAuthentication();
  }

  private _watchNavigationAndMenu(): void {
    this._router.events.pipe(untilDestroyed(this)).subscribe((event: Event): void => {
      if (event instanceof NavigationEnd) {
        this._store.dispatch(flushPendingNullableTestAnswers());
        this._languageHelperService.updateTitle(this._getPageTitle(this._router.routerState.snapshot.root));
        this.isMenuHidden = this._findRouterData(this._router.routerState.snapshot.root.firstChild, 'isMenuHidden') || false;
        this.hasFluidContainer = this._hasFluidContainer(this._router.routerState.snapshot.root);
        this.hasContainer = this._hasContainer(this._router.routerState.snapshot.root);
      }

      if (event instanceof NavigationError && event.error.status === EQpHttpStatusCode.NOT_FOUND) {
        void this._router.navigate([CommonRoutes.error404()], { replaceUrl: true });
      }
    });

    this._displayedRoutesService.menuPosition$
      .pipe(untilDestroyed(this))
      .subscribe((menuPosition: EMenuPosition): EMenuPosition => (this.currentMenuPosition = menuPosition));
  }

  private _getPageTitle(routeSnapshot: ActivatedRouteSnapshot): string {
    let title: string = routeSnapshot.data?.['pageTitle'] ? routeSnapshot.data['pageTitle'] : 'QIMAone';

    if (routeSnapshot.firstChild) {
      title = this._getPageTitle(routeSnapshot.firstChild) || title;
    }

    return title;
  }

  private _findRouterData(routeSnapshot: ActivatedRouteSnapshot, dataKey: string): boolean | undefined {
    if (routeSnapshot && Object.keys(routeSnapshot.data).length > 0 && routeSnapshot.data[dataKey] !== undefined) {
      return routeSnapshot.data[dataKey];
    } else if (routeSnapshot.firstChild) {
      return this._findRouterData(routeSnapshot.firstChild, dataKey);
    }

    return undefined;
  }

  private _initAppcues(): void {
    this._accountService
      .getAccount$()
      .pipe(
        filter(
          (account): boolean =>
            !isNil(account) &&
            this._accountService.hasAnyAuthority([
              EQpProfile.ROLE_BRAND,
              EQpProfile.ROLE_ENTITY,
              EQpProfile.ROLE_INSPECTOR,
              EQpProfile.ROLE_SERVICE_PROVIDER,
              EQpProfile.ROLE_SERVICE_PROVIDER_INSPECTOR,
            ])
        ),
        switchMap((account): Observable<void> => this._appcuesService.load$(account)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private _hasFluidContainer(routeSnapshot: Readonly<ActivatedRouteSnapshot>): boolean {
    if (routeSnapshot.firstChild) {
      return this._hasFluidContainer(routeSnapshot.firstChild);
    }

    return routeSnapshot.data?.hasFluidContainer ?? true;
  }

  private _hasContainer(routeSnapshot: Readonly<ActivatedRouteSnapshot>): boolean {
    if (routeSnapshot.firstChild) {
      return this._hasContainer(routeSnapshot.firstChild);
    }

    return routeSnapshot.data?.hasContainer ?? true;
  }

  private _storeWindowInnerHeight(): void {
    // Always use window.innerHeight (not visualViewport.height)
    // window.innerHeight represents the full viewport height excluding browser chrome
    // visualViewport.height changes when keyboard opens, which we want to avoid
    const height = this._window.innerHeight;

    this._document.documentElement.style.setProperty('--windowInnerHeight', `${height}px`);
    this._lastViewportWidth = this._window.visualViewport?.width ?? this._window.innerWidth;
  }

  private _storeWindowInnerHeightIfNotKeyboard(): void {
    // Only update height if width changed (orientation/rotation/zoom) not just height (keyboard)
    // This prevents recalculating height when iOS keyboard opens/closes
    const currentWidth = this._window.visualViewport?.width ?? this._window.innerWidth;

    if (Math.abs(currentWidth - this._lastViewportWidth) > 1) {
      // Width changed significantly - this is a real viewport change (rotation/zoom), not keyboard
      this._storeWindowInnerHeight();
    }
  }
}
